import { useCallback, useEffect, useState } from "react";

import { useDevWarnOnce } from "../../utils/useDevWarnOnce";
import { HAVE_CURRENT_DATA } from "../Video/mediaReadiness";
import { subscribeVideoEvents } from "../Video/videoEvents";

export interface RasterSize {
  readonly width: number;
  readonly height: number;
}

/**
 * 표시 중인 raster에 대해 아는 것. `frameReady`는 지금 소스에서 그릴 프레임이 한 번이라도 준비됐는지다.
 * 영상은 크기(metadata)가 첫 프레임보다 먼저 오므로 둘을 따로 둔다. 이미지는 디코딩되면 둘 다 참이다.
 */
export interface RasterMeasurement {
  readonly size: RasterSize;
  readonly frameReady: boolean;
}

/** raster 컴포넌트가 스테이지에 알리는 통로. 크기를 모르거나 내려갈 때는 `null`이다. */
export type RasterReporter = (measurement: RasterMeasurement | null) => void;

/**
 * - `measured`: 표시 중인 raster의 크기를 쟀다.
 * - `pending`: raster를 표시하지만 아직 크기를 모른다(영상 metadata·이미지 decode 대기).
 * - `declared`: 표시할 raster가 없어 소비처가 선언한 비율이 frame 공간이다.
 * - `unknown`: raster도 선언도 없다.
 */
export type AnnotationRasterGeometry = "measured" | "pending" | "declared" | "unknown";

export interface AnnotationRasterStageState {
  readonly hasRaster: boolean;
  readonly measured: RasterMeasurement | null;
  readonly declaredAspectRatio: number | null;
}

const ASPECT_RATIO_MISMATCH_TOLERANCE = 0.01;

function validAspectRatio(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
}

function sameMeasurement(left: RasterMeasurement | null, right: RasterMeasurement | null): boolean {
  if (left === right) return true;
  return (
    left?.size.width === right?.size.width &&
    left?.size.height === right?.size.height &&
    left?.frameReady === right?.frameReady
  );
}

export function toRasterSize(width: number, height: number): RasterSize | null {
  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
    ? { width, height }
    : null;
}

export function resolveAnnotationRasterGeometry(
  state: AnnotationRasterStageState,
): AnnotationRasterGeometry {
  if (state.hasRaster) return state.measured ? "measured" : "pending";
  return state.declaredAspectRatio === null ? "unknown" : "declared";
}

/**
 * 스테이지 비율. 영상·이미지는 스테이지 안에서 `object-fit: contain`으로 자기 비율을 지키므로 실제
 * 크기에서 구한 비율만이 overlay와 raster를 같은 사각형에 둔다. 선언 비율은 그 전까지의 스테이지
 * 크기만 정한다.
 */
export function resolveAnnotationStageAspectRatio(
  state: AnnotationRasterStageState,
): number | null {
  if (state.hasRaster && state.measured) {
    return state.measured.size.width / state.measured.size.height;
  }
  return state.declaredAspectRatio;
}

/**
 * overlay(contour·raster layer)를 그려도 되는가 — 결과 재생과 tracking preview가 이 판정 하나를
 * 쓴다. overlay는 정규화 좌표를 스테이지 전체에 펴므로, 스테이지가 표시 중인 raster와 같은
 * 사각형임이 확인되고 그 raster가 그릴 프레임을 가진 뒤에만 그린다. 아무것도 그리지 않은 영상 위의
 * overlay는 가리킬 대상이 없다. raster와 overlay의 정합 조건이 늘면 여기에 더한다.
 */
export function isAnnotationOverlayPresentable(state: AnnotationRasterStageState): boolean {
  if (state.hasRaster) return state.measured?.frameReady === true;
  return state.declaredAspectRatio !== null;
}

function useAspectRatioMismatchWarning(declared: number | null, measured: RasterSize | null) {
  const mismatched =
    declared !== null &&
    measured !== null &&
    Math.abs(declared / (measured.width / measured.height) - 1) > ASPECT_RATIO_MISMATCH_TOLERANCE;
  useDevWarnOnce(
    mismatched,
    `[c6] MediaAnnotationStudio: media aspectRatio ${declared} does not match the displayed media (${measured?.width}x${measured?.height}). The measured ratio is used so overlays stay on the media.`,
  );
}

/**
 * 결과 재생·tracking preview 스테이지의 geometry와 overlay 표시 여부. raster 컴포넌트는
 * `reportRaster`로 자기 크기와 프레임 준비를 알리고, 내려갈 때 `null`을 알린다.
 */
export function useAnnotationRasterStage({
  hasRaster,
  declaredAspectRatio,
}: {
  readonly hasRaster: boolean;
  readonly declaredAspectRatio?: number | null;
}) {
  const [measured, setMeasured] = useState<RasterMeasurement | null>(null);
  const reportRaster = useCallback<RasterReporter>((measurement) => {
    setMeasured((current) => (sameMeasurement(current, measurement) ? current : measurement));
  }, []);
  const declared = validAspectRatio(declaredAspectRatio);
  const state: AnnotationRasterStageState = {
    hasRaster,
    measured: hasRaster ? measured : null,
    declaredAspectRatio: declared,
  };
  useAspectRatioMismatchWarning(declared, state.measured?.size ?? null);
  const aspectRatio = resolveAnnotationStageAspectRatio(state);
  const overlayVisible = isAnnotationOverlayPresentable(state);

  return {
    aspectRatio,
    geometry: resolveAnnotationRasterGeometry(state),
    overlayVisible,
    /** raster가 있는데 아직 overlay를 그릴 수 없다 — 대기 표시의 근거다. */
    overlayPending: hasRaster && !overlayVisible,
    reportRaster,
  };
}

/**
 * `<video>`의 크기와 첫 프레임 준비를 알리는 ref. 크기는 metadata 도착·해상도 변경(`resize`)마다 다시
 * 읽는다. 첫 프레임 준비는 `loadeddata`에서 켜지고 소스가 비워지면(`emptied`) 꺼진다. 재생 중 seek나
 * 데이터 대기로 `readyState`가 잠깐 떨어져도 화면에는 마지막 프레임이 남으므로 끄지 않는다.
 * element가 내려가면 `null`을 알린다.
 */
export function useVideoRaster(
  onChange: RasterReporter | undefined,
): (element: HTMLVideoElement | null) => void {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!video || !onChange) return;
    let frameReady = video.readyState >= HAVE_CURRENT_DATA;
    const report = () => {
      const size = toRasterSize(video.videoWidth, video.videoHeight);
      onChange(size ? { size, frameReady } : null);
    };
    report();
    const unsubscribe = subscribeVideoEvents(video, {
      loadedmetadata: report,
      resize: report,
      loadeddata: () => {
        frameReady = true;
        report();
      },
      emptied: () => {
        frameReady = false;
        report();
      },
    });
    return () => {
      unsubscribe();
      onChange(null);
    };
  }, [onChange, video]);

  return setVideo;
}
